/**
 * Web Audio node helpers. Every function here works on any BaseAudioContext, so
 * the exact same synthesis code runs inside an OfflineAudioContext bake and inside
 * the live graph.
 */

export function bq(ac, type, freq, Q = 1, gain = 0) {
  const f = ac.createBiquadFilter();
  f.type = type;
  f.frequency.value = Math.max(10, Math.min(freq, ac.sampleRate * 0.48));
  f.Q.value = Q;
  if (gain) f.gain.value = gain;
  return f;
}

export function gain(ac, v = 1) {
  const g = ac.createGain();
  g.gain.value = v;
  return g;
}

/** Chain a list of nodes and return [first, last]. */
export function chain(nodes) {
  for (let i = 0; i < nodes.length - 1; i++) nodes[i].connect(nodes[i + 1]);
  return [nodes[0], nodes[nodes.length - 1]];
}

/** Play a Float32Array (or AudioBuffer) into `dest` at `when`. Returns the source. */
export function playData(ac, data, dest, when, { rate = 1, detune = 0, loop = false } = {}) {
  const src = ac.createBufferSource();
  if (data instanceof Float32Array) {
    const b = ac.createBuffer(1, data.length, ac.sampleRate);
    b.copyToChannel(data, 0);
    src.buffer = b;
  } else {
    src.buffer = data;
  }
  src.playbackRate.value = rate;
  if (detune && src.detune) src.detune.value = detune;
  src.loop = loop;
  src.connect(dest);
  src.start(when);
  return src;
}

/** tanh-ish soft clipper. Never lets a peak leave the buffer above ~0.98. */
const shaperCurves = new Map();
export function softClip(ac, drive = 1.6) {
  const key = drive.toFixed(3);
  let curve = shaperCurves.get(key);
  if (!curve) {
    const n = 2048;
    curve = new Float32Array(n);
    const norm = Math.tanh(drive);
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 2 - 1;
      curve[i] = (Math.tanh(x * drive) / norm) * 0.985;
    }
    shaperCurves.set(key, curve);
  }
  const ws = ac.createWaveShaper();
  ws.curve = curve;
  ws.oversample = '2x';
  return ws;
}

/** Asymmetric drive — adds even harmonics, makes a body sound like a physical blast. */
export function driveStage(ac, amount = 3) {
  const n = 2048;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const y = Math.tanh(x * amount) * 0.92 + 0.06 * Math.tanh(x * x * amount);
    curve[i] = Math.max(-0.99, Math.min(0.99, y));
  }
  const ws = ac.createWaveShaper();
  ws.curve = curve;
  ws.oversample = '2x';
  return ws;
}

export function compressor(ac, {
  threshold = -18, knee = 6, ratio = 4, attack = 0.003, release = 0.18,
} = {}) {
  const c = ac.createDynamicsCompressor();
  c.threshold.value = threshold;
  c.knee.value = knee;
  c.ratio.value = ratio;
  c.attack.value = attack;
  c.release.value = release;
  return c;
}

/** Brick-wall-ish limiter: fast compressor into a soft clipper. */
export function limiter(ac) {
  const c = compressor(ac, { threshold: -6, knee: 1.5, ratio: 20, attack: 0.0008, release: 0.09 });
  const s = softClip(ac, 1.35);
  c.connect(s);
  return { input: c, output: s };
}

/**
 * Schedule an exponential-ish decay on an AudioParam without the
 * exponentialRampToValueAtTime zero-crossing trap.
 */
export function decayTo(param, when, peak, tau, floor = 0.0005) {
  param.cancelScheduledValues(when);
  param.setValueAtTime(Math.max(peak, floor), when);
  param.setTargetAtTime(0, when, Math.max(0.0005, tau));
  param.setValueAtTime(0, when + tau * 8);
}

/** Attack/decay envelope on a gain param. */
export function adEnv(param, when, peak, attack, tau) {
  param.cancelScheduledValues(when);
  param.setValueAtTime(0, when);
  param.linearRampToValueAtTime(peak, when + Math.max(0.0002, attack));
  param.setTargetAtTime(0, when + attack, Math.max(0.0008, tau));
  param.setValueAtTime(0, when + attack + tau * 8);
}

/** A stereo-widening pair of delays; returns { input, output }. */
export function widener(ac, ms = 8, spread = 0.7) {
  const input = gain(ac, 1);
  const merger = ac.createChannelMerger(2);
  const dl = ac.createDelay(0.2);
  const dr = ac.createDelay(0.2);
  dl.delayTime.value = 0;
  dr.delayTime.value = ms / 1000;
  const gl = gain(ac, 1);
  const gr = gain(ac, spread);
  input.connect(dl).connect(gl).connect(merger, 0, 0);
  input.connect(dr).connect(gr).connect(merger, 0, 1);
  return { input, output: merger };
}

/** Normalise a rendered AudioBuffer in place so no sample exceeds `target`. */
export function normaliseBuffer(buffer, target = 0.985) {
  let peak = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const d = buffer.getChannelData(c);
    for (let i = 0; i < d.length; i++) { const a = Math.abs(d[i]); if (a > peak) peak = a; }
  }
  if (peak > target && peak > 1e-9) {
    const g = target / peak;
    for (let c = 0; c < buffer.numberOfChannels; c++) {
      const d = buffer.getChannelData(c);
      for (let i = 0; i < d.length; i++) d[i] *= g;
    }
  }
  return peak;
}
